import { useEffect, useRef } from 'react'
import { SearchField } from '../../../primitives/index.js'
import { useFavoriteResults, useRecentResults } from '../../atoms/searchResults.js'
import { ScrollableContainer } from '../ScrollableContainer.js'
import { Dialog } from './components/Dialog.js'
import { FavoriteResults } from './components/FavoriteResults.js'
import { NoResults } from './components/NoResults.js'
import { RecentResults } from './components/RecentResults.js'
import { SearchResult } from './components/SearchResult.js'
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation.js'
import { useSearchStories } from './hooks/useSearchStories.js'
import classes from './search-dialog.module.css'

type Props = {
  open: boolean
  onClose: () => void
  navigationDisabled?: boolean
}

export function SearchDialog(props: Props) {
  const { open, navigationDisabled } = props

  const { noResults, searchValue, searchResults, handleOnClose, onChangeSearchValue } = useSearchStories(props)

  const ref = useRef<HTMLDivElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: the effect must run every time `searchResults` changes.
  useEffect(() => {
    ref.current?.scrollTo(0, 0)
  }, [searchResults])

  const showResultList = searchResults.length > 0
  const favoriteItems = useFavoriteResults()
  const recentItems = useRecentResults()

  const { active: activeItemId } = useKeyboardNavigation({
    dialogOpen: open,
    favorites: favoriteItems,
    navigationDisabled,
    recent: recentItems,
    search: searchResults,
  })

  return (
    <Dialog onClose={handleOnClose} open={open}>
      <div className={classes.stack} style={{ height: '600px', width: '600px' }}>
        <SearchField
          autoFocus
          collapseBorder
          disableFocusVisual
          endAdornmentLabel="esc"
          onChange={onChangeSearchValue}
          value={searchValue}
        />

        <ScrollableContainer ref={ref}>
          {showResultList ? (
            <ul
              style={{
                listStyle: 'none',
                margin: '0px',
                padding: '8px 0px',
                position: 'relative',
              }}
            >
              {searchResults.map(item => (
                <SearchResult
                  activeItemId={activeItemId}
                  item={item}
                  key={item.id}
                  navigationDisabled={navigationDisabled}
                  onClose={handleOnClose}
                />
              ))}
            </ul>
          ) : noResults ? (
            <NoResults search={searchValue} />
          ) : (
            <div className={classes.stack} style={{ padding: '16px' }}>
              <FavoriteResults
                activeItemId={activeItemId}
                items={favoriteItems}
                navigationDisabled={navigationDisabled}
                onCloseSearchDialog={handleOnClose}
              />
              <RecentResults
                activeItemId={activeItemId}
                items={recentItems}
                navigationDisabled={navigationDisabled}
                onCloseSearchDialog={handleOnClose}
              />
            </div>
          )}
        </ScrollableContainer>
      </div>
    </Dialog>
  )
}
